export const ApiErrorTypes = Object.freeze({
  STREAM_VIOLATION: 'STREAM_VIOLATION', // sim stream violation string returned from the server
  STATION_LIMIT_REACHED: 'STATION_LIMIT_REACHED',
  THUMBPRINT_RADIO_NOT_ELIGIBLE: 'THUMBPRINT_RADIO_NOT_ELIGIBLE',
  LISTENER_NOT_AUTHORIZED: 'LISTENER_NOT_AUTHORIZED',
  PLAYLIST_END: 'PLAYLIST_END',
  READONLY_MODE: 'READONLY_MODE',
  INVALID_REQUEST: 'INVALID_REQUEST',
  INVALID_REQUEST_USER_ALREADY_VERIFIED: 'INVALID_REQUEST_USER_ALREADY_VERIFIED',
  STATION_CODE_INVALID: 'STATION_CODE_INVALID',
  STATION_DOES_NOT_EXIST: 'STATION_DOES_NOT_EXIST',
  LISTENER_SUSPENDED: 'LISTENER_SUSPENDED',
  CREDIT_CARD_NO_RECORD_FOUND: 'CREDIT_CARD_NO_RECORD_FOUND',
  CONTENT_HAS_EXPIRED: 'CONTENT_HAS_EXPIRED',
  // So far only observed for creating a station with random station id sometimes
  SERVER_ERROR: 'SERVER_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  UNPLAYABLE_SHUFFLE_STATION: 'UNPLAYABLE_SHUFFLE_STATION',
  // discount verification Errors
  ELIGIBILITY_VENDOR_ERROR: 'ELIGIBILITY_VENDOR_ERROR',
  ELIGIBILITY_VENDOR_ERROR_USER_ALREADY_VERIFIED: 'ELIGIBILITY_VENDOR_ERROR_USER_ALREADY_VERIFIED',
  ELIGIBILITY_VENDOR_ERROR_SERVER: 'ELIGIBILITY_VENDOR_ERROR_SERVER',
  ELIGIBILITY_VENDOR_ERROR_VERIFICATION_FAILURE: 'ELIGIBILITY_VENDOR_ERROR_VERIFICATION_FAILURE',
  TOO_MANY_REQUESTS: 'TOO_MANY_REQUESTS',
  INELIGIBLE: 'INELIGIBLE',
  UNEXPECTED_ERROR: 'UNEXPECTED_ERROR',
} as const);

export const ApiErrorFields = Object.freeze({
  STREAM_VIOLATION: [
    'activeDeviceImageId',
    'audioWarningTrack',
    'dialogueDelay',
    'dialogueHeader',
    'dialoguePrompt',
    'expiresSeconds',
  ],
} as const);

type StreamViolationErrorData = Record<typeof ApiErrorFields.STREAM_VIOLATION[number], $TSFixMe>;

interface ApiErrorData {
  errorString: keyof typeof ApiErrorTypes | '';
  message: string;
  errorCode: number;
  errorData: $TSFixMe;
}

interface ApiErrorParams {
  config: $TSFixMe;
  headers: $TSFixMe;
  status: number;
  statusText: string;
  data: ApiErrorData | (ApiErrorData & StreamViolationErrorData);
}

// Error Class to handle Api Errors resulting from call to the Pandora backend Api
class ApiError extends Error {
  config: ApiErrorParams['config'];
  headers: ApiErrorParams['headers'];
  status: ApiErrorParams['status'];
  statusText: ApiErrorParams['statusText'];
  errorType: ApiErrorData['errorString'];
  errorMessage: ApiErrorData['message'];
  errorCode: ApiErrorData['errorCode'];
  errorData: ApiErrorData['errorData'] | null;
  errorTimeStamp: number;

  action: $TSFixMe;

  constructor(
    {
      config = {},
      headers = {},
      status = 400,
      statusText = '',
      data = { errorString: '', message: '', errorCode: 0, errorData: {} },
    }: ApiErrorParams,
    action: $TSFixMe = null,
  ) {
    super();
    this.name = 'ApiError';

    // Axios specific error fields
    this.config = config;
    this.headers = Object.assign({}, headers, config.headers);
    this.status = status;
    this.statusText = statusText;

    // fields associated with returned data in the data field, when an Api error occurs
    // ErrorResponse {
    // errorCode (integer, read only): Numeric error code associated with this error , currently always 0
    // errorString (string, read only): Short string code associated with this error , used to distinguish error type
    // message (string, read only): More detailed message about why the request failed
    // }
    this.errorType = data.errorString;
    this.errorMessage = data.message;
    this.errorCode = data.errorCode;

    // additional fields
    this.errorTimeStamp = Date.now();

    // For some error types, there are additional fields provided.
    // these are stored in ApiError.errorData
    this.errorData = null;
    if (this.errorType in ApiErrorFields) {
      this.errorData = ApiErrorFields.STREAM_VIOLATION.reduce(
        (acc, errorKey) => ({
          ...acc,
          [errorKey]: (data as ApiErrorData & StreamViolationErrorData)[errorKey],
        }),
        {},
      );
    }

    // the action that resulted in the error
    if (action) {
      this.action = action;
    }
  }
}

export default ApiError;
